import FoodSafetyReportRegisters from 'src/components/food-safety/FoodSafetyReportRegisters'
import { LeftOutlined, LockOutlined } from '@ant-design/icons'
import { Button, DatePicker, Icon, Tooltip, message } from 'antd'
import moment from 'moment'
import { saveAs } from 'file-saver'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import styled from 'styled-components/macro'
import { theme } from 'src/branding/omsTheme'
import { useWindowWidth } from 'src/utils/useWindowWidth'
import { BodyText, H3 } from 'src/branding/typography'
import {
  FoodSafetyHeroBand,
  FoodSafetyPageTitle,
  FoodSafetySurfaceCard,
} from 'src/components/food-safety/FoodSafetyPageChrome'
import FadeIn from 'src/components/styled/FadeIn'
import Loading from 'src/components/styled/Loading'
import PermissionBlockScreen from 'src/components/shared/PermissionBlockScreen'
import {
  FOOD_SAFETY_HISTORY_LOCKED_CODE,
  FOOD_SAFETY_REPORTS_PAYWALL_REASON,
  FOOD_SAFETY_SECONDARY_TEXT,
  FOOD_SAFETY_SURFACE,
  FOOD_SAFETY_WEEK_PRESET,
  FOOD_SAFETY_WEEK_PRESET_LABELS,
} from 'src/constants/foodSafety'
import useIsBlocked, { useCanAccess } from 'src/hooks/useIsBlocked'
import useFoodSafetyLatestRequest from 'src/hooks/useFoodSafetyLatestRequest'
import {
  downloadFoodSafetySamplePdf,
  downloadFoodSafetyWeekPdf,
  emailFoodSafetySamplePdf,
  emailFoodSafetyWeekPdf,
  fetchFoodSafetySettings,
  fetchFoodSafetyWeekReport,
} from 'src/utils/foodSafetyApi'
import {
  trackFoodSafetyReportEmailed,
  trackFoodSafetyReportShared,
  trackFoodSafetyReportsViewed,
  trackFoodSafetySamplePdfEmailed,
  trackFoodSafetySamplePdfPreviewed,
} from 'src/utils/foodSafetyAnalytics'
import { foodSafetyReportsEmailPreflight } from 'src/utils/foodSafetyManagerEmails'
import {
  classifyFoodSafetyReportsWeek,
  foodSafetyReportCellExceptionState,
  foodSafetyReportCellLabel,
  foodSafetyReportCellMark,
  foodSafetyReportCellTitle,
  foodSafetyReportCellTone,
  foodSafetyReportRowFractionLabel,
  foodSafetyReportsExportAffordance,
  foodSafetyReportsExportLockLine,
  foodSafetyReportsWeekStatusSubtitle,
  foodSafetyThisWeekMonday,
  foodSafetyReportsGapCue,
  foodSafetyReportsMondayOf,
  foodSafetyReportsSearchWithWeek,
  formatFoodSafetyWeekRangeLabel,
  foodSafetyReportLegendParts,
  listFoodSafetyReportLegend,
  isFoodSafetyReportsExportUnlocked,
  readFoodSafetyReportsWeekParam,
  resolveFoodSafetyReportCellAction,
  resolveFoodSafetyReportsFetchParams,
  resolveFoodSafetyReportsWeekShift,
  validateFoodSafetyExportRange,
  shiftFoodSafetyWeekStart,
  sortFoodSafetyReportRows,
} from 'src/utils/foodSafetyReports'
import FoodSafetyReportsPaywallModal from 'src/screens/food-safety/FoodSafetyReportsPaywallModal'
import FoodSafetyReportExceptions from 'src/components/food-safety/FoodSafetyReportExceptions'
import FoodSafetyReportDayPanel from 'src/components/food-safety/FoodSafetyReportDayPanel'
import FoodSafetyVisuallyHidden from 'src/components/food-safety/FoodSafetyVisuallyHidden'
import useFocusReturningDrawer from 'src/hooks/useFocusReturningDrawer'
import useFoodSafetySaveLock from 'src/hooks/useFoodSafetySaveLock'
import {
  readEarlierOpenReportExceptions,
  readReportExceptions,
} from 'src/utils/foodSafetyReportExceptions'
import {
  FOOD_SAFETY_REPORT_EXPORT_KIND,
  FOOD_SAFETY_REPORT_EXPORT_MESSAGE_KEY,
  assertReportPdf,
  formatReportExportToast,
  reportDownloadedStatus,
  reportEmailedStatus,
  reportExportErrorStatus,
  reportExportPreparingStatus,
} from 'src/utils/foodSafetyReportExportFeedback'

const FREE_TABS = [
  FOOD_SAFETY_WEEK_PRESET.THIS_WEEK,
  FOOD_SAFETY_WEEK_PRESET.LAST_WEEK,
  FOOD_SAFETY_WEEK_PRESET.OLDER,
]

const FoodSafetyReportsScreen = () => {
  const blocked = useIsBlocked('food-safety')
  const canManage = useCanAccess('food-safety-manage')
  const { venueId: routeVenueId } = useParams()
  const venue = useSelector(state => state.venue?.venue)
  const venueId = String(venue?._id || routeVenueId || '')
  const history = useHistory()
  const latestReport = useFoodSafetyLatestRequest()

  const [tab, setTab] = useState(FOOD_SAFETY_WEEK_PRESET.THIS_WEEK)
  const [olderWeekStart, setOlderWeekStart] = useState(null)
  const [report, setReport] = useState(null)
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)
  const [emailing, setEmailing] = useState(false)
  const [previewingSample, setPreviewingSample] = useState(false)
  const [emailingSample, setEmailingSample] = useState(false)
  const exportLock = useFoodSafetySaveLock()
  const [paywall, setPaywall] = useState(null) // { reason, historyAccess }
  const [managerEmails, setManagerEmails] = useState(null)
  const [dayPanel, setDayPanel] = useState(null) // { localDate, section }
  const [exportFrom, setExportFrom] = useState(null)
  const [exportTo, setExportTo] = useState(null)
  const dayDrawer = useFocusReturningDrawer()
  const { show: showDayDrawer, hide: hideDayDrawer } = dayDrawer
  const [exportStatus, setExportStatus] = useState(null)
  const retryExportRef = useRef(null)
  // Venue's today from Express; browser clock only for older Express builds.
  const todayLocalDate = report?.todayLocalDate || moment().format('YYYY-MM-DD')
  const thisMonday = foodSafetyThisWeekMonday(todayLocalDate)

  const historyAccess = report?.historyAccess || paywall?.historyAccess
  const narrow = (useWindowWidth() || 0) < theme.breakpoints.md
  const [legendOpen, setLegendOpen] = useState(false)
  const legendShown = !narrow || legendOpen
  const exportUnlocked = isFoodSafetyReportsExportUnlocked(historyAccess)
  const exportAffordance = foodSafetyReportsExportAffordance(historyAccess)
  const emailPreflightBlocked =
    managerEmails != null &&
    Boolean(foodSafetyReportsEmailPreflight(managerEmails, { canManage }))

  /** Resolves true when the week loaded, false on error, null when superseded. */
  const load = useCallback(
    async (nextTab, nextOlderStart) => {
      if (!venueId) return null
      const params = resolveFoodSafetyReportsFetchParams(
        nextTab,
        nextOlderStart,
      )
      if (!params) {
        latestReport.cancel()
        setLoading(false)
        return null
      }
      const isCurrent = latestReport.begin()
      setLoading(true)
      try {
        const data = await fetchFoodSafetyWeekReport(venueId, params)
        if (!isCurrent()) return null
        setReport(data)
        if (data?.weekStart) {
          const shownTab = classifyFoodSafetyReportsWeek(data.weekStart, {
            today: data.todayLocalDate || moment().format('YYYY-MM-DD'),
          })
          setTab(shownTab)
          if (shownTab === FOOD_SAFETY_WEEK_PRESET.OLDER) {
            setOlderWeekStart(data.weekStart)
          }
          const { location } = history
          const search = foodSafetyReportsSearchWithWeek(
            location.search,
            data.weekStart,
            { todayLocalDate: data.todayLocalDate },
          )
          if (search !== (location.search || '')) {
            history.replace({ ...location, search })
          }
        }
        return true
      } catch (error) {
        if (!isCurrent()) return null
        if (error?.code === FOOD_SAFETY_HISTORY_LOCKED_CODE) {
          setPaywall({
            reason: FOOD_SAFETY_REPORTS_PAYWALL_REASON.OLDER,
            historyAccess: error.historyAccess,
          })
          // Keep last successful This/Last Week grid visible
          setTab(FOOD_SAFETY_WEEK_PRESET.THIS_WEEK)
        } else {
          console.error(error)
          message.error(
            error?.response?.data?.message ||
              error?.message ||
              'Could not load Food Safety report.',
          )
        }
        return false
      } finally {
        isCurrent.done()
        if (isCurrent()) setLoading(false)
      }
    },
    [venueId, latestReport, history],
  )

  useEffect(() => {
    if (!venueId || blocked) return undefined
    trackFoodSafetyReportsViewed({ venueId, surface: 'reports' })
    // Back from "Record corrective action" lands on the week the chef left.
    const urlWeek = readFoodSafetyReportsWeekParam(history.location.search)
    if (urlWeek) {
      load(FOOD_SAFETY_WEEK_PRESET.OLDER, urlWeek)
        .then(loaded => {
          if (loaded === false) load(FOOD_SAFETY_WEEK_PRESET.THIS_WEEK, null)
          return undefined
        })
        .catch(() => {})
    } else {
      // Keep chip in sync with the This Week load (same H4 / 29.19 as App focus)
      setTab(FOOD_SAFETY_WEEK_PRESET.THIS_WEEK)
      load(FOOD_SAFETY_WEEK_PRESET.THIS_WEEK, null)
    }
    return undefined
  }, [venueId, load, history, blocked])

  useEffect(() => {
    setManagerEmails(null)
    if (!venueId || blocked || !canManage) return undefined
    let current = true
    fetchFoodSafetySettings(venueId)
      .then(settings => {
        if (current) setManagerEmails(settings || {})
        return undefined
      })
      .catch(() => {
        if (current) setManagerEmails({})
      })
    return () => {
      current = false
    }
  }, [venueId, blocked, canManage])

  const onSelectTab = nextTab => {
    if (nextTab === FOOD_SAFETY_WEEK_PRESET.OLDER && !exportUnlocked) {
      setPaywall({
        reason: FOOD_SAFETY_REPORTS_PAYWALL_REASON.OLDER,
        historyAccess,
      })
      return
    }
    setTab(nextTab)
    if (nextTab === FOOD_SAFETY_WEEK_PRESET.OLDER) {
      // Default older browse to two weeks back from current report week
      const anchor =
        olderWeekStart ||
        shiftFoodSafetyWeekStart(report?.weekStart, -2) ||
        null
      setOlderWeekStart(anchor)
      load(nextTab, anchor)
    } else {
      load(nextTab, null)
    }
  }

  const applyWeekShift = deltaWeeks => {
    if (!report?.weekStart) return
    const result = resolveFoodSafetyReportsWeekShift(
      report.weekStart,
      deltaWeeks,
      { fullHistoryUnlocked: exportUnlocked, today: todayLocalDate },
    )
    if (!result || result.blocked === 'future') return
    if (result.blocked === 'paywall') {
      setPaywall({
        reason: FOOD_SAFETY_REPORTS_PAYWALL_REASON.OLDER,
        historyAccess,
      })
      return
    }
    setTab(result.tab)
    if (result.tab === FOOD_SAFETY_WEEK_PRESET.OLDER) {
      setOlderWeekStart(result.weekStart)
      load(result.tab, result.weekStart)
    } else {
      load(result.tab, null)
    }
  }

  const onOlderWeekChange = value => {
    if (!value) return
    const monday = foodSafetyReportsMondayOf(value)
    if (!monday) return
    // 29.50 — clamp future weeks (App already uses validRange)
    if (monday > thisMonday) return
    setOlderWeekStart(monday)
    setTab(FOOD_SAFETY_WEEK_PRESET.OLDER)
    load(FOOD_SAFETY_WEEK_PRESET.OLDER, monday)
  }

  const openExportPaywall = reason => {
    setPaywall({ reason, historyAccess })
  }

  const onCellActivate = useCallback(
    (section, cell) => {
      const action = resolveFoodSafetyReportCellAction(cell, {
        todayLocalDate,
      })
      if (!action) return
      if (action === 'paywall') {
        setPaywall({
          reason: FOOD_SAFETY_REPORTS_PAYWALL_REASON.OLDER,
          historyAccess,
        })
        return
      }
      setDayPanel({ localDate: cell.localDate, section })
      showDayDrawer()
    },
    [historyAccess, showDayDrawer, todayLocalDate],
  )

  const onDayLocked = useCallback(
    lockedAccess => {
      hideDayDrawer()
      setPaywall({
        reason: FOOD_SAFETY_REPORTS_PAYWALL_REASON.OLDER,
        historyAccess: lockedAccess || historyAccess,
      })
    },
    [hideDayDrawer, historyAccess],
  )

  /**
   * "Preparing report…" → "Report downloaded" / "Report emailed" or a clear
   * error, as a toast and in the Export card. History locks open the paywall.
   */
  const runReportExport = async ({ kind, task, retry, paywallReason }) => {
    retryExportRef.current = retry
    setExportStatus(reportExportPreparingStatus(kind))
    message.loading({
      content: reportExportPreparingStatus(kind).title,
      key: FOOD_SAFETY_REPORT_EXPORT_MESSAGE_KEY,
      duration: 0,
    })
    try {
      const done = await task()
      setExportStatus(done)
      message.success({
        content: formatReportExportToast(done),
        key: FOOD_SAFETY_REPORT_EXPORT_MESSAGE_KEY,
        duration: 4,
      })
      return true
    } catch (error) {
      if (paywallReason && error?.code === FOOD_SAFETY_HISTORY_LOCKED_CODE) {
        message.destroy()
        setExportStatus(null)
        setPaywall({
          reason: paywallReason,
          historyAccess: error.historyAccess || historyAccess,
        })
        return false
      }
      const failed = reportExportErrorStatus(error, kind)
      setExportStatus(failed)
      const notify =
        failed.state === 'warning' ? message.warning : message.error
      notify({
        content: formatReportExportToast(failed),
        key: FOOD_SAFETY_REPORT_EXPORT_MESSAGE_KEY,
        duration: 6,
      })
      return false
    }
  }

  /** Resolves true only when the sample PDF downloaded. */
  const onPreviewSample = async (source = 'reports_export') => {
    if (!venueId) return false
    setPreviewingSample(true)
    const done = await runReportExport({
      kind: FOOD_SAFETY_REPORT_EXPORT_KIND.DOWNLOAD,
      retry: () => onPreviewSample(source),
      task: async () => {
        const { blob, filename } = await downloadFoodSafetySamplePdf(venueId)
        saveAs(assertReportPdf(blob), filename)
        trackFoodSafetySamplePdfPreviewed({ source })
        return reportDownloadedStatus({ filename, sample: true })
      },
    })
    setPreviewingSample(false)
    return done
  }

  /** Resolves true only when the sample email was sent. */
  const onEmailSample = async (source = 'reports_export') => {
    if (!venueId) return false
    const blocked =
      managerEmails == null
        ? null
        : foodSafetyReportsEmailPreflight(managerEmails, {
            canManage,
          })
    if (blocked) {
      message.warning(blocked.message)
      return false
    }
    if (!exportLock.tryAcquire('email-sample')) return false
    setEmailingSample(true)
    const done = await runReportExport({
      kind: FOOD_SAFETY_REPORT_EXPORT_KIND.EMAIL,
      retry: () => onEmailSample(source),
      task: async () => {
        const data = await emailFoodSafetySamplePdf(venueId)
        trackFoodSafetySamplePdfEmailed({ source })
        return reportEmailedStatus({ data, sample: true })
      },
    })
    exportLock.release('email-sample')
    setEmailingSample(false)
    return done
  }

  const onDownload = async () => {
    if (!exportUnlocked) {
      openExportPaywall(FOOD_SAFETY_REPORTS_PAYWALL_REASON.DOWNLOAD)
      return
    }
    const params = resolveFoodSafetyReportsFetchParams(tab, olderWeekStart)
    if (!params) {
      message.warning('Pick a week to download.')
      return
    }
    if (!exportLock.tryAcquire('download')) return
    setDownloading(true)
    await runReportExport({
      kind: FOOD_SAFETY_REPORT_EXPORT_KIND.DOWNLOAD,
      paywallReason: FOOD_SAFETY_REPORTS_PAYWALL_REASON.DOWNLOAD,
      retry: onDownload,
      task: async () => {
        const { blob, filename } = await downloadFoodSafetyWeekPdf(
          venueId,
          params,
        )
        saveAs(assertReportPdf(blob), filename)
        trackFoodSafetyReportShared({ venueId, surface: 'reports' })
        return reportDownloadedStatus({ filename })
      },
    })
    exportLock.release('download')
    setDownloading(false)
  }

  const onEmail = async () => {
    if (!exportUnlocked) {
      openExportPaywall(FOOD_SAFETY_REPORTS_PAYWALL_REASON.EMAIL)
      return
    }
    const blocked =
      managerEmails == null
        ? null
        : foodSafetyReportsEmailPreflight(managerEmails, {
            canManage,
          })
    if (blocked) {
      message.warning(blocked.message)
      return
    }
    const params = resolveFoodSafetyReportsFetchParams(tab, olderWeekStart)
    if (!params) {
      message.warning('Pick a week to email.')
      return
    }
    if (!exportLock.tryAcquire('email-week')) return
    setEmailing(true)
    await runReportExport({
      kind: FOOD_SAFETY_REPORT_EXPORT_KIND.EMAIL,
      paywallReason: FOOD_SAFETY_REPORTS_PAYWALL_REASON.EMAIL,
      retry: onEmail,
      task: async () => {
        const data = await emailFoodSafetyWeekPdf(venueId, params)
        trackFoodSafetyReportEmailed({ venueId, surface: 'reports' })
        return reportEmailedStatus({ data })
      },
    })
    exportLock.release('email-week')
    setEmailing(false)
  }

  const exportRangeParams = () => {
    const problem = validateFoodSafetyExportRange(exportFrom, exportTo, {
      today: todayLocalDate,
    })
    if (problem) {
      message.warning(problem)
      return null
    }
    return { from: exportFrom, to: exportTo }
  }

  const onDownloadRange = async () => {
    if (!exportUnlocked) {
      openExportPaywall(FOOD_SAFETY_REPORTS_PAYWALL_REASON.DOWNLOAD)
      return
    }
    const params = exportRangeParams()
    if (!params) return
    if (!exportLock.tryAcquire('download-range')) return
    setDownloading(true)
    await runReportExport({
      kind: FOOD_SAFETY_REPORT_EXPORT_KIND.DOWNLOAD,
      paywallReason: FOOD_SAFETY_REPORTS_PAYWALL_REASON.DOWNLOAD,
      retry: onDownloadRange,
      task: async () => {
        const { blob, filename } = await downloadFoodSafetyWeekPdf(
          venueId,
          params,
        )
        saveAs(assertReportPdf(blob), filename)
        trackFoodSafetyReportShared({ venueId, surface: 'reports_range' })
        return reportDownloadedStatus({ filename })
      },
    })
    exportLock.release('download-range')
    setDownloading(false)
  }

  const onEmailRange = async () => {
    if (!exportUnlocked) {
      openExportPaywall(FOOD_SAFETY_REPORTS_PAYWALL_REASON.EMAIL)
      return
    }
    const blocked =
      managerEmails == null
        ? null
        : foodSafetyReportsEmailPreflight(managerEmails, {
            canManage,
          })
    if (blocked) {
      message.warning(blocked.message)
      return
    }
    const params = exportRangeParams()
    if (!params) return
    if (!exportLock.tryAcquire('email-range')) return
    setEmailing(true)
    await runReportExport({
      kind: FOOD_SAFETY_REPORT_EXPORT_KIND.EMAIL,
      paywallReason: FOOD_SAFETY_REPORTS_PAYWALL_REASON.EMAIL,
      retry: onEmailRange,
      task: async () => {
        const data = await emailFoodSafetyWeekPdf(venueId, params)
        trackFoodSafetyReportEmailed({ venueId, surface: 'reports_range' })
        return reportEmailedStatus({ data })
      },
    })
    exportLock.release('email-range')
    setEmailing(false)
  }

  const days = report?.days || []
  const rows = useMemo(
    () => sortFoodSafetyReportRows(report?.rows || []),
    [report?.rows],
  )
  const weekLabel = useMemo(
    () => formatFoodSafetyWeekRangeLabel(report?.weekStart, report?.weekEnd),
    [report?.weekStart, report?.weekEnd],
  )
  const gapCue = useMemo(
    () =>
      foodSafetyReportsGapCue(rows, {
        weekStart: report?.weekStart,
        today: todayLocalDate,
      }),
    [rows, report?.weekStart, todayLocalDate],
  )
  const canWeekNext = Boolean(
    report?.weekStart && report.weekStart < thisMonday,
  )
  const canWeekPrev = Boolean(report?.weekStart)
  const prevOpensPaywall =
    canWeekPrev &&
    !exportUnlocked &&
    resolveFoodSafetyReportsWeekShift(report?.weekStart, -1, {
      fullHistoryUnlocked: false,
      today: todayLocalDate,
    })?.blocked === 'paywall'

  if (blocked) return <PermissionBlockScreen />
  if (!venueId) return <Loading />

  return (
    <Wrapper>
      <TopBar>
        <BackLink to={`/v/${venueId}/food-safety`}>
          <LeftOutlined /> Today’s overview
        </BackLink>
      </TopBar>

      <FoodSafetyHeroBand>
        <div>
          <FoodSafetyPageTitle>Food Safety reports</FoodSafetyPageTitle>
          <BodyText>
            {foodSafetyReportsWeekStatusSubtitle(historyAccess)}
          </BodyText>
          <BodyText data-testid='food-safety-reports-staff-guidance'>
            Authorised venue staff can present these records to an inspector
            without a manager login. Use Last Week or Older Reports to find
            earlier records; available history and PDF exports follow the
            venue’s plan.
          </BodyText>
        </div>
      </FoodSafetyHeroBand>

      <TabRow role='tablist' aria-label='Report period'>
        {FREE_TABS.map(key => (
          <TabChip
            key={key}
            type='button'
            role='tab'
            aria-selected={tab === key}
            aria-label={
              key === FOOD_SAFETY_WEEK_PRESET.OLDER && !exportUnlocked
                ? `${FOOD_SAFETY_WEEK_PRESET_LABELS[key]} (Starter unlock)`
                : FOOD_SAFETY_WEEK_PRESET_LABELS[key]
            }
            data-active={tab === key ? 'true' : 'false'}
            data-testid={`food-safety-reports-tab-${key}`}
            onClick={() => onSelectTab(key)}
          >
            {FOOD_SAFETY_WEEK_PRESET_LABELS[key]}
            {key === FOOD_SAFETY_WEEK_PRESET.OLDER && !exportUnlocked ? (
              <>
                {' '}
                <LockOutlined aria-hidden />
              </>
            ) : null}
          </TabChip>
        ))}
      </TabRow>

      {tab === FOOD_SAFETY_WEEK_PRESET.OLDER && exportUnlocked ? (
        <OlderNav>
          <BodyText>Or jump to a week</BodyText>
          <DatePicker
            value={olderWeekStart ? moment(olderWeekStart, 'YYYY-MM-DD') : null}
            onChange={onOlderWeekChange}
            allowClear={false}
            format='D MMM YYYY'
            disabledDate={current => {
              if (!current) return false
              const monday = foodSafetyReportsMondayOf(current)
              return Boolean(monday && thisMonday && monday > thisMonday)
            }}
            data-testid='food-safety-reports-older-picker'
          />
        </OlderNav>
      ) : null}

      {loading && !report ? (
        <Loading />
      ) : (
        <ReportPanel>
          {weekLabel ? (
            <WeekNav data-testid='food-safety-reports-week-nav'>
              <Button
                onClick={() => applyWeekShift(-1)}
                disabled={loading || !canWeekPrev}
                aria-label={
                  prevOpensPaywall
                    ? 'Earlier weeks unlock on Starter'
                    : 'Previous week'
                }
                data-testid='food-safety-reports-week-prev'
                data-paywall={prevOpensPaywall ? 'true' : 'false'}
              >
                <Icon type='left' />
              </Button>
              <WeekLabel data-testid='food-safety-reports-week-label'>
                {weekLabel}
              </WeekLabel>
              <Button
                onClick={() => applyWeekShift(1)}
                disabled={loading || !canWeekNext}
                aria-label='Next week'
                data-testid='food-safety-reports-week-next'
              >
                <Icon type='right' />
              </Button>
            </WeekNav>
          ) : null}

          {report?.summary?.summaryLine ? (
            <SummaryLine>{report.summary.summaryLine}</SummaryLine>
          ) : null}

          {report ? (
            gapCue.kind === 'attention' ? (
              <GapStrip data-testid='food-safety-reports-gaps'>
                {gapCue.lines.map(line => (
                  <li key={line}>{line}</li>
                ))}
              </GapStrip>
            ) : (
              <GapClear data-testid='food-safety-reports-gaps-clear'>
                {gapCue.line}
              </GapClear>
            )
          ) : null}

          {!historyAccess?.fullHistoryUnlocked &&
          historyAccess?.olderRecordCount > 0 ? (
            <WindowHint>
              Free shows the last {historyAccess.freeHistoryDays || 14} days ·{' '}
              {historyAccess.olderRecordCount} older record
              {historyAccess.olderRecordCount === 1 ? '' : 's'} unlock on
              Starter.
            </WindowHint>
          ) : null}

          {narrow ? (
            <LegendToggle
              type='button'
              aria-expanded={legendOpen ? 'true' : 'false'}
              aria-controls='food-safety-reports-legend'
              onClick={() => setLegendOpen(v => !v)}
              data-testid='food-safety-reports-legend-toggle'
            >
              {legendOpen ? 'Hide key' : 'What do the marks mean?'}
            </LegendToggle>
          ) : null}
          <Legend
            id='food-safety-reports-legend'
            data-testid='food-safety-reports-legend'
            aria-label='Grid key'
            hidden={!legendShown}
          >
            {listFoodSafetyReportLegend({
              historyUnlocked: Boolean(historyAccess?.fullHistoryUnlocked),
            }).map(item => {
              const parts = foodSafetyReportLegendParts(item)
              return (
                <LegendItem key={item.key}>
                  {parts.dot ? (
                    <LegendMark aria-hidden>
                      <LegendDot data-exception={parts.dot} />
                    </LegendMark>
                  ) : parts.mark ? (
                    <LegendMark aria-hidden>{parts.mark}</LegendMark>
                  ) : null}
                  <span>{parts.label}</span>
                </LegendItem>
              )
            })}
          </Legend>

          <GridScroll>
            <Grid
              style={{
                '--day-cols': days.length || 7,
              }}
              data-testid='food-safety-reports-grid'
            >
              <Corner />
              {days.map(day => (
                <DayHead key={day.localDate}>
                  <span>{day.weekdayLabel}</span>
                  <DayDate>
                    {moment(day.localDate, 'YYYY-MM-DD').format('D')}
                  </DayDate>
                </DayHead>
              ))}
              <Corner />

              {rows.map(row => (
                <React.Fragment key={row.section}>
                  <RowLabel>{row.label}</RowLabel>
                  {row.cells.map(cell => {
                    const tone = foodSafetyReportCellTone(cell.status)
                    const exceptionState =
                      foodSafetyReportCellExceptionState(cell)
                    const action = resolveFoodSafetyReportCellAction(cell, {
                      todayLocalDate,
                    })
                    return (
                      <Cell
                        key={`${row.section}-${cell.localDate}`}
                        data-tone={tone}
                        data-navigable={action ? 'true' : 'false'}
                        title={foodSafetyReportCellTitle(cell)}
                        data-testid={`fs-cell-${row.section}-${cell.localDate}`}
                        role={action ? 'button' : undefined}
                        tabIndex={action ? 0 : undefined}
                        onClick={
                          action
                            ? () => onCellActivate(row.section, cell)
                            : undefined
                        }
                        onKeyDown={
                          action
                            ? event => {
                                if (
                                  event.key === 'Enter' ||
                                  event.key === ' '
                                ) {
                                  event.preventDefault()
                                  onCellActivate(row.section, cell)
                                }
                              }
                            : undefined
                        }
                      >
                        <span aria-hidden>
                          {foodSafetyReportCellMark(cell)}
                        </span>
                        <FoodSafetyVisuallyHidden
                          data-testid={`fs-cell-label-${row.section}-${cell.localDate}`}
                        >
                          {foodSafetyReportCellLabel(cell, {
                            sectionLabel: row.label,
                          })}
                        </FoodSafetyVisuallyHidden>
                        {exceptionState ? (
                          <CellDot
                            data-exception={exceptionState}
                            data-testid={`fs-cell-exception-${row.section}-${cell.localDate}`}
                            aria-hidden
                          />
                        ) : null}
                      </Cell>
                    )
                  })}
                  <Fraction>
                    {foodSafetyReportRowFractionLabel(row.weekSummary)}
                  </Fraction>
                </React.Fragment>
              ))}
            </Grid>
          </GridScroll>

          <FoodSafetyReportRegisters
            registers={report?.registers}
            venueId={venueId}
          />

          {readReportExceptions(report) ? (
            <FoodSafetyReportExceptions
              venueId={venueId}
              exceptions={readReportExceptions(report)}
              earlierOpen={readEarlierOpenReportExceptions(report)}
              summary={report?.summary}
            />
          ) : null}

          {loading ? <QuietLoading>Updating…</QuietLoading> : null}
        </ReportPanel>
      )}

      <ExportCard data-testid='food-safety-reports-export'>
        <H3>Export</H3>
        <BodyText>{foodSafetyReportsExportLockLine(historyAccess)}</BodyText>
        {canManage && exportUnlocked ? (
          <SetupHint to={`/v/${venueId}/food-safety/setup`}>
            {emailPreflightBlocked
              ? 'Add report emails in setup'
              : 'Change report emails'}
          </SetupHint>
        ) : null}
        <ExportActions>
          {exportAffordance.locked ? (
            <>
              <Tooltip title={exportAffordance.title} placement='top'>
                <span>
                  <ExportButton
                    icon='lock'
                    onClick={onEmail}
                    loading={emailing}
                    data-testid='food-safety-reports-email'
                    data-locked='true'
                    data-chrome='quiet'
                    aria-label={`${exportAffordance.emailLabel} — ${exportAffordance.title}`}
                  >
                    {exportAffordance.emailLabel}
                  </ExportButton>
                </span>
              </Tooltip>
              <Tooltip title={exportAffordance.title} placement='top'>
                <span>
                  <ExportButton
                    icon='lock'
                    onClick={onDownload}
                    loading={downloading}
                    data-testid='food-safety-reports-download'
                    data-locked='true'
                    data-chrome='quiet'
                    aria-label={`${exportAffordance.downloadLabel} — ${exportAffordance.title}`}
                  >
                    {exportAffordance.downloadLabel}
                  </ExportButton>
                </span>
              </Tooltip>
              <Tooltip title={exportAffordance.sampleHint} placement='top'>
                <span>
                  <SampleButton
                    onClick={() => onPreviewSample('reports_export')}
                    loading={previewingSample}
                    data-testid='food-safety-reports-sample'
                    aria-label={exportAffordance.sampleLabel}
                  >
                    {exportAffordance.sampleLabel}
                  </SampleButton>
                </span>
              </Tooltip>
              <Tooltip title={exportAffordance.sampleEmailHint} placement='top'>
                <span>
                  <SampleButton
                    onClick={() => onEmailSample('reports_export')}
                    loading={emailingSample}
                    data-testid='food-safety-reports-sample-email'
                    aria-label={exportAffordance.sampleEmailLabel}
                  >
                    {exportAffordance.sampleEmailLabel}
                  </SampleButton>
                </span>
              </Tooltip>
            </>
          ) : (
            <>
              <ExportButton
                icon='mail'
                onClick={onEmail}
                loading={emailing}
                data-testid='food-safety-reports-email'
                data-locked={emailPreflightBlocked ? 'true' : 'false'}
                data-chrome={emailPreflightBlocked ? 'quiet' : undefined}
                aria-label={
                  emailPreflightBlocked
                    ? 'Email — add a report email first'
                    : exportAffordance.emailLabel
                }
              >
                {exportAffordance.emailLabel}
              </ExportButton>
              <ExportButton
                icon='download'
                onClick={onDownload}
                loading={downloading}
                data-testid='food-safety-reports-download'
                data-locked='false'
                aria-label={exportAffordance.downloadLabel}
              >
                {exportAffordance.downloadLabel}
              </ExportButton>
            </>
          )}
        </ExportActions>
        {exportUnlocked ? (
          <RangeExport
            data-testid='food-safety-reports-range'
            ref={node => {
              const inputs = node?.querySelectorAll?.('input') || []
              ;['Inspection start date', 'Inspection end date'].forEach(
                (name, index) => {
                  inputs[index]?.setAttribute('aria-label', name)
                },
              )
            }}
          >
            <RangeExportLabel>Inspection range</RangeExportLabel>
            <DatePicker.RangePicker
              format='D MMM YYYY'
              allowClear
              value={
                exportFrom && exportTo
                  ? [
                      moment(exportFrom, 'YYYY-MM-DD'),
                      moment(exportTo, 'YYYY-MM-DD'),
                    ]
                  : null
              }
              onChange={values => {
                const start = values?.[0]
                const end = values?.[1]
                setExportFrom(start ? start.format('YYYY-MM-DD') : null)
                setExportTo(end ? end.format('YYYY-MM-DD') : null)
              }}
              disabledDate={current =>
                Boolean(
                  current &&
                    current.isAfter(
                      moment(todayLocalDate, 'YYYY-MM-DD'),
                      'day',
                    ),
                )
              }
              data-testid='food-safety-reports-range-picker'
            />
            <ExportActions>
              <ExportButton
                icon='mail'
                onClick={onEmailRange}
                loading={emailing}
                data-testid='food-safety-reports-range-email'
                data-chrome='quiet'
              >
                Email range
              </ExportButton>
              <ExportButton
                icon='download'
                onClick={onDownloadRange}
                loading={downloading}
                data-testid='food-safety-reports-range-download'
                data-chrome='quiet'
              >
                Download range
              </ExportButton>
            </ExportActions>
          </RangeExport>
        ) : null}
        {exportStatus ? (
          <ExportStatus
            role={exportStatus.state === 'error' ? 'alert' : 'status'}
            aria-live='polite'
            data-state={exportStatus.state}
            data-testid='food-safety-reports-export-status'
          >
            <strong>{exportStatus.title}</strong>
            {exportStatus.detail ? <span>{exportStatus.detail}</span> : null}
            {exportStatus.state === 'error' &&
            exportStatus.canRetry &&
            retryExportRef.current ? (
              <Button
                size='small'
                onClick={() => retryExportRef.current?.()}
                data-testid='food-safety-reports-export-retry'
              >
                Try again
              </Button>
            ) : null}
          </ExportStatus>
        ) : null}
      </ExportCard>

      {dayDrawer.mounted && dayPanel ? (
        <FoodSafetyReportDayPanel
          venueId={venueId}
          localDate={dayPanel.localDate}
          focusSection={dayPanel.section}
          open={dayDrawer.open}
          onClose={dayDrawer.hide}
          afterVisibleChange={dayDrawer.afterVisibleChange}
          onLocked={onDayLocked}
        />
      ) : null}

      <FoodSafetyReportsPaywallModal
        visible={Boolean(paywall)}
        reason={paywall?.reason}
        olderRecordCount={paywall?.historyAccess?.olderRecordCount}
        upgradeHint={paywall?.historyAccess?.upgradeHint}
        onClose={() => setPaywall(null)}
        onPreviewSample={async () => {
          if (await onPreviewSample('paywall')) setPaywall(null)
        }}
        previewingSample={previewingSample}
        onEmailSample={async () => {
          if (await onEmailSample('paywall')) setPaywall(null)
        }}
        emailingSample={emailingSample}
      />
    </Wrapper>
  )
}

const Wrapper = styled(FadeIn)`
  width: 100%;
  max-width: 72rem;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.sm};
  box-sizing: border-box;

  ${({ theme }) => theme.device('md')`
    padding: ${theme.spacing.md};
  `};
`

const TopBar = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  color: ${({ theme }) => theme.colours.lettuceGreen || '#7cb305'};
  font-weight: 600;
`

const TabRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`

const TabChip = styled.button`
  border: 1px solid ${({ theme }) => theme.colours.panSilver || '#ddd'};
  background: #fff;
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  padding: 0.35rem 0.85rem;
  cursor: pointer;
  font-size: 0.9rem;

  &[data-active='true'] {
    border-color: ${FOOD_SAFETY_SURFACE.LIME_EDGE};
    background: ${FOOD_SAFETY_SURFACE.LIME_WASH};
    font-weight: 600;
  }
`

const ReportPanel = styled(FoodSafetySurfaceCard)`
  margin-bottom: ${({ theme }) => theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: 0;
`

const OlderNav = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`

const WeekNav = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.xs};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`

const WeekLabel = styled.p`
  margin: 0;
  font-weight: 600;
  min-width: 11rem;
  text-align: center;
`

const SummaryLine = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const GapStrip = styled.ul`
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
  padding: 0.5rem 0.75rem;
  list-style: none;
  border-left: 3px solid #c47a12;
  background: #fffbeb;
  font-size: 0.875rem;
  font-weight: 600;
  color: #8a5a0a;

  li + li {
    margin-top: 0.2rem;
  }
`

const GapClear = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
  padding: 0.35rem 0;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const WindowHint = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
  font-size: 0.85rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const Legend = styled.ul`
  &[hidden] {
    display: none;
  }
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 0.85rem;
  list-style: none;
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
  padding: 0;
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colours.oliveGrey || '#6b6b6b'};
`

const LegendToggle = styled.button`
  align-self: flex-start;
  margin: 0 0 ${({ theme }) => theme.spacing.tiny};
  padding: 0;
  border: 0;
  background: none;
  font-size: 0.8rem;
  font-weight: 600;
  text-decoration: underline;
  color: ${({ theme }) => theme.colours.oliveGrey || '#6b6b6b'};
  cursor: pointer;
`

const LegendItem = styled.li`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
`

const LegendMark = styled.span`
  font-weight: 700;
  min-width: 1rem;
  text-align: center;
  color: ${({ theme }) => theme.colours.charcoal || '#333'};
`

const GridScroll = styled.div`
  overflow-x: auto;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`

const Grid = styled.div`
  display: grid;
  grid-template-columns:
    minmax(6.5rem, 1.4fr) repeat(var(--day-cols), minmax(2.2rem, 1fr))
    minmax(3rem, 0.7fr);
  gap: 0.25rem;
  min-width: 32rem;
  align-items: center;
`

const Corner = styled.div``

const DayHead = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.oliveGrey || '#6b6b6b'};
`

const DayDate = styled.span`
  font-weight: 400;
`

const RowLabel = styled.div`
  font-size: 0.85rem;
  font-weight: 600;
  padding-right: 0.35rem;
`

const Cell = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 2.75rem;
  height: 2.75rem;
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  border: 1px solid ${({ theme }) => theme.colours.panSilver || '#eee'};
  font-weight: 700;
  font-size: 0.85rem;
  letter-spacing: -0.02em;
  background: #fff;

  &[data-navigable='true'] {
    cursor: pointer;
  }

  &[data-tone='ok'] {
    color: ${FOOD_SAFETY_SURFACE.STATUS_DONE};
    background: ${FOOD_SAFETY_SURFACE.LIME_WASH};
    border-color: ${FOOD_SAFETY_SURFACE.LIME_EDGE};
  }
  &[data-tone='alert'] {
    color: ${FOOD_SAFETY_SURFACE.STATUS_ATTENTION};
    background: #fff2e8;
    border-color: #ffbb96;
  }
  &[data-tone='warn'] {
    color: #ad6800;
    background: #fffbe6;
    border-color: #ffe58f;
  }
  &[data-tone='locked'] {
    color: ${({ theme }) => theme.colours.oliveGrey || '#6b6b6b'};
    background: #fafafa;
  }
  &[data-tone='muted'] {
    color: ${({ theme }) => theme.colours.oliveGrey || '#aaa'};
  }
  &[data-tone='paused'] {
    color: ${({ theme }) => theme.colours.oliveGrey || '#6b6b6b'};
    background: repeating-linear-gradient(
      135deg,
      #fafafa,
      #fafafa 6px,
      #f0f0f0 6px,
      #f0f0f0 8px
    );
  }
`

/** Exception overlay — filled amber when unresolved, green ring once actioned. */
const exceptionDotStyles = `
  display: inline-block;
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 50%;
  box-sizing: border-box;

  &[data-exception='unresolved'] {
    background: ${FOOD_SAFETY_SURFACE.STATUS_ATTENTION};
  }
  &[data-exception='actioned'] {
    background: #fff;
    border: 2px solid ${FOOD_SAFETY_SURFACE.STATUS_DONE};
  }
`

const CellDot = styled.span`
  position: absolute;
  top: 0.25rem;
  right: 0.25rem;
  ${exceptionDotStyles}
`

const LegendDot = styled.span`
  ${exceptionDotStyles}
`

const Fraction = styled.div`
  font-size: 0.8rem;
  color: ${({ theme }) => theme.colours.oliveGrey || '#6b6b6b'};
  text-align: right;
  padding-left: 0.25rem;
`

const QuietLoading = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
  font-size: 0.85rem;
  color: ${({ theme }) => theme.colours.oliveGrey || '#6b6b6b'};
`

const ExportCard = styled(FoodSafetySurfaceCard)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`

const RangeExport = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.xs};
  padding-top: ${({ theme }) => theme.spacing.sm};
  border-top: 1px solid ${({ theme }) => theme.colours.panSilver || '#eee'};
`

const RangeExportLabel = styled.p`
  margin: 0;
  font-size: 0.85rem;
  font-weight: 600;
`

const SetupHint = styled(Link)`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.lettuceGreen || '#7cb305'};
  align-self: flex-start;
`

const ExportStatus = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem 0.6rem;
  margin-top: ${({ theme }) => theme.spacing.sm};
  padding: 0.5rem 0.75rem;
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};
  border: 1px solid ${({ theme }) => theme.colours.panSilver || '#eee'};
  font-size: 0.85rem;

  span {
    color: ${FOOD_SAFETY_SECONDARY_TEXT};
  }
  &[data-state='done'] {
    border-color: ${FOOD_SAFETY_SURFACE.LIME_EDGE};
    background: ${FOOD_SAFETY_SURFACE.LIME_WASH};
  }
  &[data-state='error'],
  &[data-state='warning'] {
    border-color: ${FOOD_SAFETY_SURFACE.STATUS_ATTENTION};
    background: #fff7f0;
  }
`

const ExportActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing.sm};
`

/** Free: outline + muted — not a primary that looks broken then paywalls. */
const ExportButton = styled(Button)`
  &[data-locked='true'] {
    color: ${({ theme }) => theme.colours.oliveGrey || '#6b6b6b'};
    border-color: ${({ theme }) => theme.colours.panSilver || '#d9d9d9'};
    background: #fafafa;
    font-weight: 500;

    &:hover,
    &:focus {
      color: ${({ theme }) => theme.colours.oliveGrey || '#6b6b6b'};
      border-color: ${({ theme }) => theme.colours.oliveGrey || '#bfbfbf'};
      background: #f5f5f5;
    }
  }
`

/** Free taste CTA — quieter than Upgrade, louder than locked Unlock buttons. */
const SampleButton = styled(Button)`
  color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
  border-color: ${({ theme }) => theme.colours.lettuceGreen || '#b5d922'};
  background: #fff;
  font-weight: 600;

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.colours.seaweedGreen || '#132d07'};
    border-color: ${({ theme }) => theme.colours.lettuceGreen || '#b5d922'};
    background: ${({ theme }) =>
      theme.colours.lightGreen || 'rgba(180, 216, 32, 0.12)'};
  }
`

export default FoodSafetyReportsScreen
